import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Box, Paper, useMediaQuery } from '@mui/material';

import { sortEffortsByStatusAndDate } from 'src/utils/statusUtils';
import MobileCard from 'src/pages/Reports/components/MobileCard';
import EffortRow from 'src/pages/Reports/components/EffortRow';

const EffortsTable = ({
  filteredQueries = [], // Pre-filtered queries
  filteredExternalEfforts = [], // Pre-filtered external efforts
  onRemoveExternalEffort,
  onEditExternalEffort,
  company,
  showActions = true,
  statusType = 'inProgress', // 'wins' or 'inProgress' - used for styling and text only
  onDateUpdate,
  dateRanges,
  dateFilter,
  allReports,
  allReportsMutate,
  allowDateEdit = true,
}) => {
  const isMobile = useMediaQuery('(max-width: 600px)');

  // Sort the pre-filtered data
  const sortedQueries = sortEffortsByStatusAndDate(filteredQueries);
  const sortedExternalEfforts = sortEffortsByStatusAndDate(filteredExternalEfforts, 'date');

  // Check if any effort has a link (for dynamic column rendering)
  const hasAnyLink = sortedQueries.some((q) => q.mediaURL) || sortedExternalEfforts.some((eff) => eff.link);

  // Mobile layout
  if (isMobile) {
    return (
      <Box>
        {/* Regular queries */}
        {sortedQueries.map((query) => (
          <MobileCard
            key={query.id}
            query={query}
            showActions={showActions}
            statusType={statusType}
            onDateUpdate={onDateUpdate}
            company={company}
            dateRanges={dateRanges}
            dateFilter={dateFilter}
            allReports={allReports}
            allReportsMutate={allReportsMutate}
            allowDateEdit={allowDateEdit}
          />
        ))}

        {/* External efforts */}
        {sortedExternalEfforts.map((effort) => (
          <MobileCard
            key={effort.id}
            query={effort}
            isExternal={true}
            onRemoveExternalEffort={onRemoveExternalEffort}
            onEditExternalEffort={onEditExternalEffort}
            company={company}
            showActions={showActions}
            statusType={statusType}
            onDateUpdate={onDateUpdate}
            dateRanges={dateRanges}
            dateFilter={dateFilter}
            allReports={allReports}
            allReportsMutate={allReportsMutate}
            allowDateEdit={allowDateEdit}
          />
        ))}
      </Box>
    );
  }

  // Desktop table layout
  return (
    <Paper sx={{ width: '100%', overflow: 'auto', border: '1px solid #ddd' }}>
      <Box
        sx={{
          display: 'flex',
          p: 1,
          bgcolor: 'neutral.100',
          borderBottom: '1px solid #ddd',
        }}
      >
        <Typography variant="subtitle2" sx={{ flex: 1.5, pr: 2 }}>
          Media Outlet
        </Typography>
        <Typography variant="subtitle2" sx={{ flex: 4, pr: 2 }}>
          Query Title
        </Typography>
        <Typography variant="subtitle2" sx={{ flex: 1, pr: 2 }}>
          Date
        </Typography>
        <Typography variant="subtitle2" sx={{ flex: 1, pr: 2 }}>
          Status
        </Typography>
        {(hasAnyLink || showActions) && (
          <Typography variant="subtitle2" sx={{ flex: 1.5, pr: 2 }}>
            Action
          </Typography>
        )}
      </Box>

      {/* Regular queries */}
      {sortedQueries.map((query) => (
        <EffortRow
          key={query.id}
          effort={query}
          isExternal={false}
          showActions={showActions}
          statusType={statusType}
          onDateUpdate={onDateUpdate}
          company={company}
          dateRanges={dateRanges}
          dateFilter={dateFilter}
          allReports={allReports}
          allReportsMutate={allReportsMutate}
          allowDateEdit={allowDateEdit}
        />
      ))}

      {/* External efforts */}
      {sortedExternalEfforts.map((effort) => (
        <EffortRow
          key={effort.id}
          effort={effort}
          isExternal={true}
          onRemoveExternalEffort={onRemoveExternalEffort}
          onEditExternalEffort={onEditExternalEffort}
          company={company}
          showActions={showActions}
          statusType={statusType}
          onDateUpdate={onDateUpdate}
          dateRanges={dateRanges}
          dateFilter={dateFilter}
          allReports={allReports}
          allReportsMutate={allReportsMutate}
          allowDateEdit={allowDateEdit}
        />
      ))}
    </Paper>
  );
};

EffortsTable.propTypes = {
  filteredQueries: PropTypes.array.isRequired,
  filteredExternalEfforts: PropTypes.array.isRequired,
  onRemoveExternalEffort: PropTypes.func,
  onEditExternalEffort: PropTypes.func,
  company: PropTypes.string,
  showActions: PropTypes.bool,
  statusType: PropTypes.oneOf(['wins', 'inProgress']).isRequired,
  onDateUpdate: PropTypes.func,
  dateRanges: PropTypes.object,
  dateFilter: PropTypes.string,
  allReports: PropTypes.array,
  allReportsMutate: PropTypes.func,
  allowDateEdit: PropTypes.bool,
};

export default EffortsTable;
